import { BaseGrid } from './grid/BaseGrid';
import { SquareGrid } from './grid/SquareGrid';
import { HexGrid } from './grid/HexGrid';
import { IsometricGrid } from './grid/IsometricGrid';
import { BaseGridRenderer, DEFAULT_COLORS, FlowDirection } from './grid/BaseGridRenderer';
import { Cell } from './grid/Cell';
import { SquareGridRenderer } from './grid/SquareGridRenderer';
import { HexGridRenderer } from './grid/HexGridRenderer';
import { IsometricGridRenderer } from './grid/IsometricGridRenderer';
import { GridInteraction, DrawMode } from './grid/GridInteraction';
import { GridType } from './grid/GridType';
import { BezierGrid } from './grid/BezierGrid';
import { BezierGridRenderer } from './grid/BezierGridRenderer';
import { BezierControlPointEditor } from './grid/BezierControlPointEditor';
import { BezierLayoutPreset, getLayoutConfig } from './grid/BezierLayoutPresets';
import { HexOrientation } from './grid/HexMath';
import { PathFinder } from './pathfinding/core/PathFinder';
import { AStar } from './pathfinding/algorithms/AStar';
import { Dijkstra } from './pathfinding/algorithms/Dijkstra';
import { BFS } from './pathfinding/algorithms/BFS';
import { DFS } from './pathfinding/algorithms/DFS';
import { BestFirst } from './pathfinding/algorithms/BestFirst';
import { JumpPointSearch } from './pathfinding/algorithms/JumpPointSearch';
import { FlowField } from './pathfinding/algorithms/FlowField';
import { HierarchicalAStar } from './pathfinding/hierarchical/HierarchicalAStar';
import { AnimationController, StepInfo } from './visualization/AnimationController';
import { CellState } from './grid/Cell';
import { ViewportController } from './visualization/ViewportController';
import { HierarchicalOverlayRenderer } from './visualization/HierarchicalOverlayRenderer';
import { ColorGenerator } from './utils/ColorGenerator';

export class PathfindingApp {
  private grid: BaseGrid;
  private renderer: BaseGridRenderer;
  private interaction: GridInteraction;
  private animationController: AnimationController;
  private viewportController: ViewportController;
  private algorithms: Map<string, PathFinder>;
  private currentAlgorithm: PathFinder;
  private currentGridType: GridType = GridType.SQUARE;

  private canvas: HTMLCanvasElement;
  private gridTypeSelect: HTMLSelectElement;
  private algorithmSelect: HTMLSelectElement;
  private toolButtons: NodeListOf<HTMLButtonElement>;
  private clearGridBtn: HTMLButtonElement;
  private clearPathBtn: HTMLButtonElement;
  private runSolutionBtn: HTMLButtonElement;
  private runStepBtn: HTMLButtonElement;
  private stepControlsSection: HTMLElement;
  private statisticsPanel: HTMLElement;
  private stepBackBtn: HTMLButtonElement;
  private stepForwardBtn: HTMLButtonElement;
  private resetBtn: HTMLButtonElement;
  private speedSlider: HTMLInputElement;
  private speedDisplay: HTMLSpanElement;
  private currentStepSpan: HTMLSpanElement;
  private openListSizeSpan: HTMLSpanElement;
  private closedListSizeSpan: HTMLSpanElement;
  private pathLengthSpan: HTMLSpanElement;
  private allowDiagonalCheckbox: HTMLInputElement;
  private gridSettingsSection: HTMLElement;
  private showFlowArrowsCheckbox: HTMLInputElement;
  private flowFieldSettingsSection: HTMLElement;
  private hpaSettingsSection: HTMLElement;
  private clusterSizeSlider: HTMLInputElement;
  private currentFlowFieldData: Map<Cell, FlowDirection> | null = null;
  private showFlowArrows: boolean = true;
  private clusterColors: Map<number, string> = new Map();
  private bezierEditor: BezierControlPointEditor | null = null;
  private showControlPointsCheckbox: HTMLInputElement;
  private showPatchBordersCheckbox: HTMLInputElement;
  private bezierLayoutSelect: HTMLSelectElement;
  private bezierSettingsSection: HTMLElement;

  constructor() {
    this.canvas = document.getElementById('grid-canvas') as HTMLCanvasElement;
    this.gridTypeSelect = document.getElementById('grid-type-select') as HTMLSelectElement;
    this.algorithmSelect = document.getElementById('algorithm-select') as HTMLSelectElement;
    this.toolButtons = document.querySelectorAll('[data-mode="paint"], [data-mode="start"], [data-mode="goal"]') as NodeListOf<HTMLButtonElement>;
    this.clearGridBtn = document.getElementById('clear-grid') as HTMLButtonElement;
    this.clearPathBtn = document.getElementById('clear-path') as HTMLButtonElement;
    this.runSolutionBtn = document.getElementById('run-solution') as HTMLButtonElement;
    this.runStepBtn = document.getElementById('run-step') as HTMLButtonElement;
    this.stepControlsSection = document.getElementById('step-controls-section') as HTMLElement;
    this.statisticsPanel = document.getElementById('statistics-panel') as HTMLElement;
    this.stepBackBtn = document.getElementById('step-back') as HTMLButtonElement;
    this.stepForwardBtn = document.getElementById('step-forward') as HTMLButtonElement;
    this.resetBtn = document.getElementById('reset') as HTMLButtonElement;
    this.speedSlider = document.getElementById('speed-slider') as HTMLInputElement;
    this.speedDisplay = document.getElementById('speed-display') as HTMLSpanElement;
    this.currentStepSpan = document.getElementById('current-step') as HTMLSpanElement;
    this.openListSizeSpan = document.getElementById('open-list-size') as HTMLSpanElement;
    this.closedListSizeSpan = document.getElementById('closed-list-size') as HTMLSpanElement;
    this.pathLengthSpan = document.getElementById('path-length') as HTMLSpanElement;
    this.allowDiagonalCheckbox = document.getElementById('allow-diagonal') as HTMLInputElement;
    this.gridSettingsSection = document.getElementById('grid-settings-section') as HTMLElement;
    this.showFlowArrowsCheckbox = document.getElementById('show-flow-arrows') as HTMLInputElement;
    this.flowFieldSettingsSection = document.getElementById('flowfield-settings-section') as HTMLElement;
    this.hpaSettingsSection = document.getElementById('hpa-settings-section') as HTMLElement;
    this.clusterSizeSlider = document.getElementById('cluster-size-slider') as HTMLInputElement;
    this.showControlPointsCheckbox = document.getElementById('show-control-points') as HTMLInputElement;
    this.showPatchBordersCheckbox = document.getElementById('show-patch-borders') as HTMLInputElement;
    this.bezierLayoutSelect = document.getElementById('bezier-layout-select') as HTMLSelectElement;
    this.bezierSettingsSection = document.getElementById('bezier-settings-section') as HTMLElement;

    const { grid, renderer } = this.createGrid(GridType.SQUARE);
    this.grid = grid;
    this.renderer = renderer;

    this.viewportController = new ViewportController(this.canvas);
    this.interaction = new GridInteraction(this.grid, this.renderer, this.viewportController);
    this.animationController = new AnimationController(this.renderer);
    this.setupInteractionCallback();

    this.algorithms = new Map<string, PathFinder>([
      ['astar', new AStar()],
      ['dijkstra', new Dijkstra()],
      ['bfs', new BFS()],
      ['dfs', new DFS()],
      ['bestfirst', new BestFirst()],
      ['jps', new JumpPointSearch()],
      ['flowfield', new FlowField()],
      ['hpastar', new HierarchicalAStar()]
    ]);

    this.currentAlgorithm = this.algorithms.get('astar')!;

    this.initializeDefaultGrid();
    this.attachEventListeners();
    this.setupAnimationCallbacks();
    this.setupDraggablePanels();
    this.setupCollapsiblePanels();
    this.setupViewportCallbacks();
    this.updateGridSettingsVisibility();
    this.updateBezierSettingsVisibility();
    this.updateFlowFieldSettingsVisibility();
    this.updateHPASettingsVisibility();
    this.renderer.render();
  }

  private createGrid(gridType: GridType): { grid: BaseGrid; renderer: BaseGridRenderer } {
    let grid: BaseGrid;
    let renderer: BaseGridRenderer;
    const cellSize = 15;

    switch (gridType) {
      case GridType.SQUARE:
        grid = new SquareGrid({ rows: 40, cols: 60, allowDiagonal: true });
        renderer = new SquareGridRenderer(grid as SquareGrid, {
          canvas: this.canvas,
          cellSize: cellSize,
          showGrid: true,
          colors: DEFAULT_COLORS
        });
        break;

      case GridType.HEX_POINTY:
        const pointyRows = 34;
        const pointyCols = 48;
        grid = new HexGrid({ rows: pointyRows, cols: pointyCols, orientation: HexOrientation.POINTY });
        renderer = new HexGridRenderer(grid as HexGrid, {
          canvas: this.canvas,
          cellSize: cellSize,
          showGrid: true,
          colors: DEFAULT_COLORS
        });
        break;

      case GridType.HEX_FLAT:
        const flatRows = 28;
        const flatCols = 52;
        grid = new HexGrid({ rows: flatRows, cols: flatCols, orientation: HexOrientation.FLAT });
        renderer = new HexGridRenderer(grid as HexGrid, {
          canvas: this.canvas,
          cellSize: cellSize,
          showGrid: true,
          colors: DEFAULT_COLORS
        });
        break;

      case GridType.ISOMETRIC:
        const isoRows = 30;
        const isoCols = 30;
        grid = new IsometricGrid({ rows: isoRows, cols: isoCols, allowDiagonal: false });
        renderer = new IsometricGridRenderer(grid as IsometricGrid, {
          canvas: this.canvas,
          cellSize: cellSize,
          showGrid: true,
          colors: DEFAULT_COLORS
        });
        break;

      case GridType.BEZIER:
        const bezierPreset = this.bezierLayoutSelect?.value as BezierLayoutPreset ?? 'island';
        const layoutCfg = getLayoutConfig(bezierPreset);
        grid = new BezierGrid({
          patchRows: 12,
          patchCols: 8,
          patchCountX: layoutCfg.patchCountX,
          patchCountY: layoutCfg.patchCountY,
          canvasWidth: 960,
          canvasHeight: 640,
          preset: bezierPreset,
          activePatchMask: layoutCfg.activePatchMask
        });
        renderer = new BezierGridRenderer(grid as BezierGrid, {
          canvas: this.canvas,
          cellSize: cellSize,
          showGrid: true,
          colors: DEFAULT_COLORS
        });
        break;

      default:
        throw new Error(`Unsupported grid type: ${gridType}`);
    }

    return { grid, renderer };
  }

  private switchGridType(gridType: GridType): void {
    if (this.currentGridType === gridType) return;

    this.currentGridType = gridType;

    if (this.bezierEditor) {
      this.bezierEditor.destroy();
      this.bezierEditor = null;
    }

    if (this.interaction) {
      this.interaction.destroy();
    }

    if (this.animationController) {
      this.animationController.destroy();
    }

    if (this.viewportController) {
      this.viewportController.destroy();
    }

    const { grid, renderer } = this.createGrid(gridType);
    this.grid = grid;
    this.renderer = renderer;

    this.viewportController = new ViewportController(this.canvas);
    this.viewportController.resetTransform();

    if (gridType === GridType.BEZIER) {
      this.showControlPointsCheckbox.checked = false;

      // Sync patch borders checkbox with renderer's initial state
      if (this.renderer instanceof BezierGridRenderer) {
        this.renderer.setShowPatchBorders(this.showPatchBordersCheckbox.checked);
      }

      this.bezierEditor = new BezierControlPointEditor(
        this.grid as BezierGrid,
        this.renderer as BezierGridRenderer,
        this.viewportController
      );
    }

    this.interaction = new GridInteraction(this.grid, this.renderer, this.viewportController);
    this.animationController = new AnimationController(this.renderer);
    this.setupAnimationCallbacks();
    this.setupViewportCallbacks();
    this.setupInteractionCallback();
    this.currentFlowFieldData = null;

    this.initializeDefaultGrid();
    this.hideStepControls();
    this.updateStatistics({ currentStep: 0, openListSize: 0, closedListSize: 0, isComplete: false });
    this.hideStatisticsPanel();
    this.renderer.render();
  }

  private initializeDefaultGrid(): void {
    const midRow = Math.floor(this.grid.rows / 2);

    let startCol: number;
    let goalCol: number;

    if (this.grid.gridType === GridType.SQUARE) {
      startCol = Math.floor(this.grid.cols / 4);
      goalCol = Math.floor(this.grid.cols * 3 / 4);
    } else if (this.grid.gridType === GridType.HEX_POINTY || this.grid.gridType === GridType.HEX_FLAT) {
      startCol = Math.floor(this.grid.cols / 6);
      goalCol = Math.floor(this.grid.cols * 5 / 6);
    } else if (this.grid.gridType === GridType.BEZIER) {
      const bezierGrid = this.grid as BezierGrid;
      if (bezierGrid.patchCountY > 1) {
        // L-shaped layout: place start in top-left patch, goal in bottom-right patch
        const startRow = Math.floor(bezierGrid.patchRows / 2);
        const startC = Math.floor(bezierGrid.patchCols / 2);
        const goalRow = bezierGrid.patchRows + Math.floor(bezierGrid.patchRows / 2);
        const goalC = bezierGrid.patchCols + Math.floor(bezierGrid.patchCols / 2);
        this.grid.setStart(startRow, startC);
        this.grid.setGoal(goalRow, goalC);
        return;
      }
      startCol = Math.floor(this.grid.cols / 4);
      goalCol = Math.floor(this.grid.cols * 3 / 4);
    } else {
      startCol = Math.floor(this.grid.cols / 4);
      goalCol = Math.floor(this.grid.cols * 3 / 4);
    }

    this.grid.setStart(midRow, startCol);
    this.grid.setGoal(midRow, goalCol);
  }

  private attachEventListeners(): void {
    this.gridTypeSelect.addEventListener('change', () => {
      const gridType = this.gridTypeSelect.value as GridType;
      this.switchGridType(gridType);
      this.updateGridSettingsVisibility();
      this.updateBezierSettingsVisibility();
    });

    this.algorithmSelect.addEventListener('change', () => {
      const selectedAlgorithm = this.algorithms.get(this.algorithmSelect.value);
      if (selectedAlgorithm) {
        this.currentAlgorithm = selectedAlgorithm;
        this.updateFlowFieldSettingsVisibility();
        this.updateHPASettingsVisibility();
      }
    });

    this.allowDiagonalCheckbox.addEventListener('change', () => {
      this.updateDiagonalSetting();
    });

    this.showFlowArrowsCheckbox.addEventListener('change', () => {
      this.showFlowArrows = this.showFlowArrowsCheckbox.checked;
      this.renderWithFlowField();
    });

    this.showControlPointsCheckbox.addEventListener('change', () => {
      this.updateBezierControlPointsVisibility();
    });

    this.showPatchBordersCheckbox.addEventListener('change', () => {
      if (this.renderer instanceof BezierGridRenderer) {
        this.renderer.setShowPatchBorders(this.showPatchBordersCheckbox.checked);
      }
    });

    this.bezierLayoutSelect.addEventListener('change', () => {
      if (this.currentGridType === GridType.BEZIER) {
        this.switchBezierLayout();
      }
    });

    this.clusterSizeSlider?.addEventListener('input', () => {
      const clusterSizeDisplay = document.getElementById('cluster-size-display');
      if (clusterSizeDisplay) {
        clusterSizeDisplay.textContent = this.clusterSizeSlider.value;
      }
    });

    this.toolButtons.forEach(button => {
      button.addEventListener('click', () => this.selectTool(button));
    });

    this.clearGridBtn.addEventListener('click', () => this.clearGrid());
    this.clearPathBtn.addEventListener('click', () => this.clearPath());
    this.runSolutionBtn.addEventListener('click', () => this.runSolution());
    this.runStepBtn.addEventListener('click', () => this.handleStepButton());

    this.stepBackBtn.addEventListener('click', () => this.animationController.stepBackward());
    this.stepForwardBtn.addEventListener('click', () => this.animationController.stepForward());
    this.resetBtn.addEventListener('click', () => this.resetStepMode());

    this.speedSlider.addEventListener('input', () => {
      const ips = parseInt(this.speedSlider.value);
      this.animationController.setIterationsPerSecond(ips);
      this.speedDisplay.textContent = `${ips} IPS`;
    });
  }

  private setupAnimationCallbacks(): void {
    this.animationController.onStateChangeCallback((state) => {
      if (state === 'playing') {
        this.runStepBtn.textContent = '⏸';
        this.runStepBtn.title = 'Pause auto-stepping';
      } else if (this.animationController.isActive()) {
        this.runStepBtn.textContent = '▶';
        this.runStepBtn.title = 'Auto step through visualization';
      } else {
        this.runStepBtn.textContent = '⏯';
        this.runStepBtn.title = 'Start step-by-step visualization';
      }
      this.updateStepButtonStates();
    });

    this.animationController.onStepChangeCallback((info: StepInfo) => {
      this.updateStatistics(info);
      this.updateStepButtonStates();
    });

    this.animationController.onPostRenderCallback(() => {
      if (this.currentAlgorithm instanceof FlowField && this.showFlowArrows) {
        this.buildFlowFieldFromSteps();
        if (this.currentFlowFieldData && this.currentFlowFieldData.size > 0) {
          this.renderer.renderFlowField(this.currentFlowFieldData);
        }
      }

      if (this.currentAlgorithm instanceof HierarchicalAStar) {
        const hpaData = this.currentAlgorithm.getVisualizationData();
        if (hpaData.clusters.length > 0) {
          const ctx = this.canvas.getContext('2d');
          if (!ctx) return;

          if (this.clusterColors.size === 0) {
            this.clusterColors = ColorGenerator.generateClusterColors(hpaData.clusters.length);
          }

          HierarchicalOverlayRenderer.renderClusters(
            ctx,
            hpaData.clusters,
            this.renderer.getCellSize(),
            this.clusterColors
          );

          HierarchicalOverlayRenderer.renderEntrancePoints(
            ctx,
            hpaData.entrances,
            this.renderer.getCellSize()
          );

          const stepHistory = this.animationController.getStepHistory();
          const currentStepIndex = this.animationController.getCurrentStep() - 1;
          if (
            currentStepIndex >= 0 &&
            currentStepIndex < stepHistory.length &&
            stepHistory[currentStepIndex]?.metadata?.phase === 'abstract_search' &&
            hpaData.abstractGraph
          ) {
            HierarchicalOverlayRenderer.renderAbstractGraph(
              ctx,
              hpaData.abstractGraph,
              this.renderer.getCellSize()
            );
          }
        }
      }
    });
  }

  private updateStepButtonStates(): void {
    this.stepBackBtn.disabled = !this.animationController.canStepBackward();
    this.stepForwardBtn.disabled = !this.animationController.canStepForward();
  }

  private setupDraggablePanels(): void {
    const panels = document.querySelectorAll('.floating-panel');

    panels.forEach(panel => {
      const header = panel.querySelector('.panel-header') as HTMLElement;
      if (!header) return;

      let isDragging = false;
      let startX: number;
      let startY: number;
      let initialX: number;
      let initialY: number;

      const convertToAbsolutePosition = (panelEl: HTMLElement) => {
        const rect = panelEl.getBoundingClientRect();
        panelEl.style.left = `${rect.left}px`;
        panelEl.style.top = `${rect.top}px`;
        panelEl.style.bottom = 'auto';
        panelEl.style.right = 'auto';
        panelEl.style.transform = 'none';
      };

      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('collapse-btn')) return;

        const panelEl = panel as HTMLElement;
        const rect = panelEl.getBoundingClientRect();

        convertToAbsolutePosition(panelEl);
        panelEl.classList.add('dragging');

        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;
        initialX = rect.left;
        initialY = rect.top;

        e.preventDefault();
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        (panel as HTMLElement).style.left = `${initialX + dx}px`;
        (panel as HTMLElement).style.top = `${initialY + dy}px`;
      };

      const handleMouseUp = () => {
        if (isDragging) {
          (panel as HTMLElement).classList.remove('dragging');
        }
        isDragging = false;
      };

      header.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
  }

  private setupCollapsiblePanels(): void {
    const collapseButtons = document.querySelectorAll('.collapse-btn');

    collapseButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const panelId = (button as HTMLElement).getAttribute('data-panel');
        const panel = document.getElementById(panelId!);
        if (panel) {
          panel.classList.toggle('collapsed');
          const btn = button as HTMLElement;
          btn.textContent = panel.classList.contains('collapsed') ? '+' : '−';
        }
      });
    });
  }

  private setupViewportCallbacks(): void {
    this.viewportController.onTransformChangeCallback(() => {
      this.renderer.render();
    });

    this.viewportController.onScaleChangeCallback((scale: number) => {
      this.renderer.setZoomScale(scale);
    });
  }

  private selectTool(button: HTMLButtonElement): void {
    this.toolButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    const tool = button.getAttribute('data-mode') as string;

    switch (tool) {
      case 'paint':
        this.interaction.setDrawMode(DrawMode.PAINT);
        break;
      case 'start':
        this.interaction.setDrawMode(DrawMode.START);
        break;
      case 'goal':
        this.interaction.setDrawMode(DrawMode.GOAL);
        break;
    }
  }

  private runSolution(): void {
    const start = this.grid.getStart();
    const goal = this.grid.getGoal();

    if (!start || !goal) {
      alert('Please set both start and goal points');
      return;
    }

    this.animationController.stop();
    this.grid.clearPath();
    this.grid.reset();
    this.hideStepControls();
    this.currentFlowFieldData = null;

    const result = this.currentAlgorithm.findPath(this.grid, start, goal);

    if (this.currentAlgorithm instanceof FlowField) {
      const flowData = this.currentAlgorithm.getFlowFieldData();
      if (flowData) {
        this.currentFlowFieldData = flowData.directions;
      }
    }

    if (result.success) {
      for (const cell of result.path) {
        if (!cell.isStart && !cell.isGoal) {
          cell.state = CellState.PATH;
        }
      }
      this.renderWithFlowField();

      this.updateStatistics({
        currentStep: result.steps.length,
        openListSize: 0,
        closedListSize: result.nodesExplored,
        isComplete: true,
        path: result.path
      });
      this.showStatisticsPanel();
    } else {
      alert('No path found!');
      this.renderWithFlowField();

      this.updateStatistics({
        currentStep: result.steps.length,
        openListSize: 0,
        closedListSize: result.nodesExplored,
        isComplete: true
      });
      this.showStatisticsPanel();
    }
  }

  private handleStepButton(): void {
    const state = this.animationController.getState();

    if (!this.animationController.isActive()) {
      this.startStepVisualization();
    } else if (state === 'playing') {
      this.animationController.pause();
    } else {
      this.animationController.play();
    }
  }

  private startStepVisualization(): void {
    const start = this.grid.getStart();
    const goal = this.grid.getGoal();

    if (!start || !goal) {
      alert('Please set both start and goal points');
      return;
    }

    this.grid.clearPath();
    this.grid.reset();
    this.currentFlowFieldData = null;

    if (this.currentAlgorithm instanceof FlowField) {
      this.currentFlowFieldData = new Map();
    }

    this.renderer.render();

    const generator = this.currentAlgorithm.findPathStepByStep(this.grid, start, goal);
    this.animationController.loadGenerator(generator);

    this.showStepControls();
    this.showStatisticsPanel();
    this.updateStepButtonStates();
  }

  private resetStepMode(): void {
    this.animationController.stop();
    this.grid.reset();
    this.currentFlowFieldData = null;
    this.renderer.render();
    this.hideStepControls();
    this.updateStatistics({ currentStep: 0, openListSize: 0, closedListSize: 0, isComplete: false });
  }

  private clearGrid(): void {
    this.animationController.stop();
    this.grid.clear();
    this.currentFlowFieldData = null;
    this.initializeDefaultGrid();
    this.renderer.render();
    this.updateStatistics({ currentStep: 0, openListSize: 0, closedListSize: 0, isComplete: false });
    this.hideStepControls();
    this.hideStatisticsPanel();
  }

  private clearPath(): void {
    this.animationController.stop();
    this.grid.clearPath();
    this.currentFlowFieldData = null;
    this.renderer.render();
    this.hideStepControls();
    this.hideStatisticsPanel();
    this.updateStatistics({ currentStep: 0, openListSize: 0, closedListSize: 0, isComplete: false });
  }

  private showStepControls(): void {
    if (this.stepControlsSection) {
      this.stepControlsSection.style.display = 'block';
    }
  }

  private hideStepControls(): void {
    if (this.stepControlsSection) {
      this.stepControlsSection.style.display = 'none';
    }
  }

  private showStatisticsPanel(): void {
    if (this.statisticsPanel) {
      this.statisticsPanel.style.display = 'block';
    }
  }

  private hideStatisticsPanel(): void {
    if (this.statisticsPanel) {
      this.statisticsPanel.style.display = 'none';
    }
  }

  private updateStatistics(info: StepInfo): void {
    this.currentStepSpan.textContent = info.currentStep.toString();
    this.openListSizeSpan.textContent = info.openListSize.toString();
    this.closedListSizeSpan.textContent = info.closedListSize.toString();
    this.pathLengthSpan.textContent = info.path ? info.path.length.toString() : '-';

    if (this.currentAlgorithm instanceof HierarchicalAStar) {
      const hpaStats = this.currentAlgorithm.getStatistics();

      const showHPAStats = info.isComplete;

      const hpaTimeBreakdown = document.getElementById('hpa-time-breakdown');
      const hpaSearchSpace = document.getElementById('hpa-search-space');
      const hpaMemory = document.getElementById('hpa-memory');
      const hpaQuality = document.getElementById('hpa-quality');

      if (hpaTimeBreakdown) hpaTimeBreakdown.style.display = showHPAStats ? 'block' : 'none';
      if (hpaSearchSpace) hpaSearchSpace.style.display = showHPAStats ? 'block' : 'none';
      if (hpaMemory) hpaMemory.style.display = showHPAStats ? 'block' : 'none';
      if (hpaQuality) hpaQuality.style.display = showHPAStats ? 'block' : 'none';

      if (showHPAStats) {
        const updateElement = (id: string, value: string) => {
          const el = document.getElementById(id);
          if (el) el.textContent = value;
        };

        updateElement('preprocessing-time', `${hpaStats.preprocessingTime.toFixed(2)}ms`);
        updateElement('abstract-search-time', `${hpaStats.abstractSearchTime.toFixed(2)}ms`);
        updateElement('refinement-time', `${hpaStats.refinementTime.toFixed(2)}ms`);
        updateElement('abstract-nodes', hpaStats.abstractNodesExplored.toString());
        updateElement('local-nodes', hpaStats.localNodesExplored.toString());
        updateElement(
          'total-nodes-explored',
          (hpaStats.abstractNodesExplored + hpaStats.localNodesExplored).toString()
        );
        updateElement('peak-open-list', hpaStats.peakOpenListSize.toString());
        updateElement('peak-closed-list', hpaStats.peakClosedListSize.toString());
        updateElement('cache-size', hpaStats.cacheSize.toString());
        updateElement(
          'cache-hit-rate',
          `${(hpaStats.cacheHitRate * 100).toFixed(1)}%`
        );
      }
    } else {
      const hpaTimeBreakdown = document.getElementById('hpa-time-breakdown');
      const hpaSearchSpace = document.getElementById('hpa-search-space');
      const hpaMemory = document.getElementById('hpa-memory');
      const hpaQuality = document.getElementById('hpa-quality');

      if (hpaTimeBreakdown) hpaTimeBreakdown.style.display = 'none';
      if (hpaSearchSpace) hpaSearchSpace.style.display = 'none';
      if (hpaMemory) hpaMemory.style.display = 'none';
      if (hpaQuality) hpaQuality.style.display = 'none';
    }
  }

  private updateGridSettingsVisibility(): void {
    const showDiagonalSetting =
      this.currentGridType === GridType.SQUARE ||
      this.currentGridType === GridType.ISOMETRIC;

    if (this.gridSettingsSection) {
      this.gridSettingsSection.style.display = showDiagonalSetting ? 'block' : 'none';
    }

    if (showDiagonalSetting) {
      const grid = this.grid as SquareGrid | IsometricGrid;
      this.allowDiagonalCheckbox.checked = grid.allowDiagonal;
    }
  }

  private updateDiagonalSetting(): void {
    const allowDiagonal = this.allowDiagonalCheckbox.checked;

    if (this.currentGridType === GridType.SQUARE) {
      (this.grid as SquareGrid).allowDiagonal = allowDiagonal;
    } else if (this.currentGridType === GridType.ISOMETRIC) {
      (this.grid as IsometricGrid).allowDiagonal = allowDiagonal;
    }
  }

  private updateFlowFieldSettingsVisibility(): void {
    const isFlowField = this.currentAlgorithm instanceof FlowField;
    if (this.flowFieldSettingsSection) {
      this.flowFieldSettingsSection.style.display = isFlowField ? 'block' : 'none';
    }
  }

  private updateHPASettingsVisibility(): void {
    const isHPA = this.currentAlgorithm instanceof HierarchicalAStar;
    if (this.hpaSettingsSection) {
      this.hpaSettingsSection.style.display = isHPA ? 'block' : 'none';
    }
  }

  private updateBezierSettingsVisibility(): void {
    const isBezier = this.currentGridType === GridType.BEZIER;
    if (this.bezierSettingsSection) {
      this.bezierSettingsSection.style.display = isBezier ? 'block' : 'none';
    }
  }

  private updateBezierControlPointsVisibility(): void {
    const show = this.showControlPointsCheckbox.checked;
    if (this.renderer instanceof BezierGridRenderer) {
      this.renderer.setShowControlPoints(show);
    }
    if (this.bezierEditor) {
      this.bezierEditor.setEnabled(show);
    }
  }

  private switchBezierLayout(): void {
    if (this.bezierEditor) {
      this.bezierEditor.destroy();
      this.bezierEditor = null;
    }

    if (this.interaction) {
      this.interaction.destroy();
    }

    if (this.animationController) {
      this.animationController.destroy();
    }

    if (this.viewportController) {
      this.viewportController.destroy();
    }

    const { grid, renderer } = this.createGrid(GridType.BEZIER);
    this.grid = grid;
    this.renderer = renderer;

    this.viewportController = new ViewportController(this.canvas);
    this.viewportController.resetTransform();

    this.showControlPointsCheckbox.checked = false;

    // Sync patch borders checkbox with renderer's initial state
    if (this.renderer instanceof BezierGridRenderer) {
      this.renderer.setShowPatchBorders(this.showPatchBordersCheckbox.checked);
    }

    this.bezierEditor = new BezierControlPointEditor(
      this.grid as BezierGrid,
      this.renderer as BezierGridRenderer,
      this.viewportController
    );

    this.interaction = new GridInteraction(this.grid, this.renderer, this.viewportController);
    this.animationController = new AnimationController(this.renderer);
    this.setupAnimationCallbacks();
    this.setupViewportCallbacks();
    this.setupInteractionCallback();
    this.currentFlowFieldData = null;

    this.initializeDefaultGrid();
    this.hideStepControls();
    this.updateStatistics({ currentStep: 0, openListSize: 0, closedListSize: 0, isComplete: false });
    this.hideStatisticsPanel();
    this.renderer.render();
  }

  private renderWithFlowField(): void {
    this.renderer.render();
    if (this.showFlowArrows && this.currentFlowFieldData && this.currentFlowFieldData.size > 0) {
      this.renderer.renderFlowField(this.currentFlowFieldData);
    }
  }

  private buildFlowFieldFromSteps(): void {
    if (!(this.currentAlgorithm instanceof FlowField)) return;
    if (!this.currentFlowFieldData) {
      this.currentFlowFieldData = new Map();
    }

    const stepHistory = this.animationController.getStepHistory();
    for (const step of stepHistory) {
      if (step.metadata?.direction && step.cell) {
        this.currentFlowFieldData.set(step.cell, step.metadata.direction);
      }
    }
  }

  private setupInteractionCallback(): void {
    this.interaction.setPostRenderCallback(() => {
      if (this.showFlowArrows && this.currentFlowFieldData && this.currentFlowFieldData.size > 0) {
        this.renderer.renderFlowField(this.currentFlowFieldData);
      }
    });
  }
}
